import type { MutableRefObject, ReactElement } from 'react';
import { useRef, useState } from 'react';
import { Inline, SiteAuxiliaryButton } from '@etg/wings';
import { observeClickOutside } from 'react-ref-observe';
import { Link } from 'react-router-dom';
import { css } from '@eti/styles';
import { useCloseOnEsc } from '../../../utils/useCloseOnEsc';
import { useCloseOnFocusOut } from '../../../utils/useCloseOnFocusOut';
import { useNavigateOnArrowKeys } from '../../../utils/useNavigateOnArrowKeys';
import { routes } from '../../../constants/routesAndApisConstants';
import DropdownMenu from '../../../widgets/dropdown-menu/DropdownMenu';

const listStyles = css`
  flex-direction: column;
  left: 50%;
  list-style: none;
  margin-top: 2px;
  transform: translateX(-50%);
  width: max-content;
  z-index: 1;
`;

interface MenuItem {
  name: string;
  path: string;
  target: string;
}

interface SiteButtonWithDropdownMenuProps {
  className?: string;
  hideLabel?: boolean;
  iconPrefix?: ReactElement;
  label: string;
  menu: MenuItem[];
  menuId: string;
  withBorder?: boolean;
  wrapperRef: MutableRefObject<HTMLElement | null>;
}

const SiteButtonWithDropdownMenu = ({
  className,
  hideLabel,
  iconPrefix,
  label,
  menu,
  menuId,
  withBorder,
  wrapperRef,
}: SiteButtonWithDropdownMenuProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownItemRef = useRef<HTMLAnchorElement | null>(null);

  observeClickOutside([wrapperRef], () => setIsDropdownOpen(false));
  useCloseOnEsc(dropdownRef, () => setIsDropdownOpen(false), buttonRef);
  useCloseOnFocusOut(wrapperRef, () => setIsDropdownOpen(false));
  const { handleKeyDown } = useNavigateOnArrowKeys({
    buttonRef,
    dropdownRef,
    isSubMenuOpen: isDropdownOpen,
  });

  return (
    <>
      <SiteAuxiliaryButton
        aria-controls={menuId}
        aria-expanded={isDropdownOpen}
        className={className}
        data-testid={`menu-button-${menuId}`}
        hideLabel={hideLabel}
        iconPrefix={iconPrefix}
        onClick={() => setIsDropdownOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        ref={buttonRef}
        withBorder={withBorder}
      >
        {label}
      </SiteAuxiliaryButton>
      {isDropdownOpen && (
        <DropdownMenu
          aria-hidden={!isDropdownOpen}
          className={listStyles}
          data-testid={`dropdown-menu-${menuId}`}
          id={menuId}
          ref={dropdownRef}
        >
          {menu.map(({ name, path, target }, index) => (
            <DropdownMenu.Item key={`${menuId}-dropdown-item-${index}`}>
              {Object.values(routes).some((r) => path.endsWith(r)) ? (
                <Inline
                  as={Link}
                  data-testid={`menu-link-${name}`}
                  onClick={() => setIsDropdownOpen(false)}
                  onKeyDown={handleKeyDown}
                  ref={index === 0 ? dropdownItemRef : null}
                  target={target}
                  to={path.replace(window.spa_variables.basePath, '')}
                >
                  {name}
                </Inline>
              ) : (
                <Inline
                  as="a"
                  data-testid={`menu-link-${name}`}
                  href={path}
                  onKeyDown={handleKeyDown}
                  ref={index === 0 ? dropdownItemRef : null}
                  rel="noreferrer"
                  target={target}
                >
                  {name}
                </Inline>
              )}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu>
      )}
    </>
  );
};

export default SiteButtonWithDropdownMenu;
